/**
 * A tab strip over a set of panels that already exist in the DOM. One panel
 * is shown at a time; the chosen tab is remembered per browser so a player
 * who lives in Achievements doesn't get bounced back to Upgrades each visit.
 */

export interface Tab {
  /** Initial label; `setLabel` can change it later (e.g. to add a count). */
  label: string;
  panel: HTMLElement;
}

export interface TabsView {
  setLabel(index: number, label: string): void;
  /** Hides or shows a tab; a hidden tab that was selected falls back to the first. */
  setHidden(index: number, hidden: boolean): void;
  /** Switches to a tab, as if its button had been clicked. */
  select(index: number): void;
}

const STORAGE_PREFIX = 'dinoclicker.tab';

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className: string,
  text?: string,
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
}

/**
 * `name` keeps each strip's chosen tab separate in storage; a page with two
 * strips must pass different names.
 */
export function createTabs(strip: HTMLElement, tabs: readonly Tab[], name = 'side'): TabsView {
  const storageKey = `${STORAGE_PREFIX}.${name}`;
  strip.setAttribute('role', 'tablist');
  const buttons = tabs.map((tab, i) => {
    const button = el('button', 'tabs__tab', tab.label);
    button.type = 'button';
    button.setAttribute('role', 'tab');
    tab.panel.id ||= `tab-panel-${i}`;
    button.setAttribute('aria-controls', tab.panel.id);
    tab.panel.setAttribute('role', 'tabpanel');
    strip.append(button);
    return button;
  });

  function select(index: number): void {
    tabs.forEach((tab, i) => {
      const on = i === index;
      tab.panel.hidden = !on;
      buttons[i]?.setAttribute('aria-selected', String(on));
    });
    try {
      window.localStorage.setItem(storageKey, String(index));
    } catch {
      // Storage may be unavailable; the tab still switches.
    }
  }

  let initial = 0;
  try {
    const saved = Number(window.localStorage.getItem(storageKey));
    if (Number.isInteger(saved) && saved >= 0 && saved < tabs.length) initial = saved;
  } catch {
    // Fall back to the first tab.
  }
  select(initial);

  let selected = initial;
  const selectAndRemember = (index: number): void => {
    selected = index;
    select(index);
  };
  buttons.forEach((button, i) => button.addEventListener('click', () => selectAndRemember(i)));

  return {
    setLabel(index, label) {
      const button = buttons[index];
      if (button && button.textContent !== label) button.textContent = label;
    },
    setHidden(index, hidden) {
      const button = buttons[index];
      if (!button || button.hidden === hidden) return;
      button.hidden = hidden;
      if (hidden && selected === index) selectAndRemember(0);
    },
    select(index) {
      if (index !== selected) selectAndRemember(index);
    },
  };
}
